/**
 * SavedPromptsPicker — dropdown w pasku promptu generatora.
 * Pokazuje zapisane prompty + opcję zapisu aktualnego promptu.
 */
import { useEffect, useRef, useState } from "react";
import { usePromptsLibrary } from "@/hooks/usePromptsLibrary";
import { useRecentPrompts } from "@/hooks/useRecentPrompts";
import { summarizeImagePrompt } from "@shared/image-prompt-summary";
import {
  Bookmark,
  BookmarkPlus,
  Trash2,
  Pencil,
  Check,
  Clock,
  Download,
  Upload,
  Star,
  Copy,
} from "lucide-react";

interface SavedPromptsPickerProps {
  currentPrompt: string;
  onSelect: (text: string) => void;
}

export default function SavedPromptsPicker({ currentPrompt, onSelect }: SavedPromptsPickerProps) {
  const { prompts, savePrompt, deletePrompt, updatePrompt, importBulk } = usePromptsLibrary();
  const { recent, clearRecent } = useRecentPrompts();
  const importFileRef = useRef<HTMLInputElement>(null);
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const allTags = Array.from(new Set(prompts.flatMap((p) => p.tags ?? []))).sort();
  const lq = query.trim().toLowerCase();
  const visiblePrompts = prompts.filter((p) => {
    if (tagFilter && !p.tags?.includes(tagFilter)) return false;
    if (lq && !p.label.toLowerCase().includes(lq) && !p.text.toLowerCase().includes(lq))
      return false;
    return true;
  });
  const [open, setOpen] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", handle);
    return () => window.removeEventListener("mousedown", handle);
  }, [open]);

  const trimmed = currentPrompt.trim();
  const canSave = trimmed.length > 0 && !prompts.some((p) => p.text === trimmed);

  // Cmd/Ctrl+S → save current prompt (if not already saved)
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        // Only intercept when there's a non-empty unsaved prompt visible.
        if (!canSave) return;
        e.preventDefault();
        savePrompt({ text: trimmed });
      }
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [canSave, trimmed, savePrompt]);

  return (
    <div ref={containerRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={`Zapisane prompty (${prompts.length})`}
        className="flex items-center gap-1 px-2 py-1 rounded-lg border border-white/[0.08] bg-white/[0.04] text-white/55 hover:text-white hover:border-white/[0.15] text-[11px] font-medium transition-all"
      >
        <Bookmark className="w-3 h-3" />
        {prompts.length > 0 && <span className="text-[10px]">{prompts.length}</span>}
      </button>
      {open && (
        <div className="absolute right-0 bottom-full mb-1 z-30 min-w-[280px] max-h-80 overflow-y-auto rounded-xl border border-white/[0.1] bg-[#0a0c10] py-1 shadow-2xl shadow-black/70">
          {canSave && (
            <button
              onClick={() => {
                savePrompt({ text: trimmed });
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-[11px] text-emerald-200 hover:bg-emerald-600/10 transition-colors border-b border-white/[0.06]"
            >
              <BookmarkPlus className="w-3 h-3" />
              <span className="truncate">Zapisz aktualny prompt</span>
            </button>
          )}
          {(prompts.length > 0 || true) && (
            <div className="flex items-center gap-1 px-2 py-1 border-b border-white/[0.06] bg-white/[0.02]">
              <input
                ref={importFileRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  e.target.value = "";
                  try {
                    const parsed = JSON.parse(await file.text()) as Record<string, unknown>;
                    const items = Array.isArray((parsed as { prompts?: unknown }).prompts)
                      ? ((parsed as { prompts: unknown[] }).prompts as Array<{
                          text: string;
                          label?: string;
                          tags?: string[];
                        }>)
                      : Array.isArray(parsed)
                        ? (parsed as Array<{ text: string; label?: string; tags?: string[] }>)
                        : [];
                    const added = importBulk(items);
                    alert(`Zaimportowano ${added} promptów (pominięto duplikaty)`);
                  } catch (err) {
                    alert(`Błąd importu: ${err instanceof Error ? err.message : String(err)}`);
                  }
                }}
              />
              <button
                onClick={() => importFileRef.current?.click()}
                title="Importuj prompty z pliku JSON"
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-[10px] text-white/55 hover:text-white hover:bg-white/[0.04] transition-colors"
              >
                <Upload className="w-3 h-3" /> Importuj
              </button>
              {prompts.length > 0 && (
                <button
                  onClick={() => {
                    const exportData = {
                      version: 1,
                      type: "prompts-library",
                      exportedAt: new Date().toISOString(),
                      count: prompts.length,
                      prompts: prompts.map((p) => ({ text: p.text, label: p.label, tags: p.tags })),
                    };
                    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
                      type: "application/json",
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `prompts-${new Date().toISOString().slice(0, 10)}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  title="Eksportuj wszystkie prompty"
                  className="flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-[10px] text-white/55 hover:text-white hover:bg-white/[0.04] transition-colors"
                >
                  <Download className="w-3 h-3" /> Eksportuj ({prompts.length})
                </button>
              )}
            </div>
          )}
          {recent.length > 0 && (
            <>
              <div className="flex items-center justify-between px-3 py-1 bg-white/[0.02] border-b border-white/[0.04]">
                <span className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-white/40 font-semibold">
                  <Clock className="w-2.5 h-2.5" /> Ostatnio użyte
                </span>
                <button
                  onClick={clearRecent}
                  className="text-[9px] text-white/30 hover:text-white/60 transition-colors"
                  title="Wyczyść historię"
                >
                  Wyczyść
                </button>
              </div>
              {recent.map((r) => (
                <button
                  key={r.usedAt}
                  onClick={() => {
                    onSelect(r.text);
                    setOpen(false);
                  }}
                  className="w-full text-left px-3 py-1.5 hover:bg-white/[0.04] transition-colors"
                >
                  {/* VP-179: w historii mogą siedzieć zapisane wcześniej struktury JSON. */}
                  <p className="text-[10px] text-white/65 truncate">
                    {summarizeImagePrompt(r.text)}
                  </p>
                  <p className="text-[8px] text-white/25 mt-0.5">
                    {new Date(r.usedAt).toLocaleString("pl-PL", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </p>
                </button>
              ))}
              {prompts.length > 0 && (
                <div className="px-3 py-1 bg-white/[0.02] border-y border-white/[0.04] text-[9px] uppercase tracking-wider text-white/40 font-semibold flex items-center gap-1">
                  <Bookmark className="w-2.5 h-2.5" /> Zapisane
                </div>
              )}
            </>
          )}
          {prompts.length > 5 && (
            <div className="px-2 py-1.5 border-b border-white/[0.06]">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Szukaj promptu..."
                className="w-full px-2 py-1 bg-white/[0.04] border border-white/[0.08] rounded text-[11px] text-white placeholder:text-white/30 focus:outline-none focus:border-white/20"
              />
            </div>
          )}
          {allTags.length > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 border-b border-white/[0.04] flex-wrap">
              <span className="text-[9px] uppercase text-white/40 font-semibold mr-1">Tagi:</span>
              {allTags.map((tag) => {
                const isActive = tagFilter === tag;
                return (
                  <button
                    key={tag}
                    onClick={() => setTagFilter((prev) => (prev === tag ? null : tag))}
                    className={`px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors ${
                      isActive
                        ? "bg-amber-500/20 text-amber-200 border border-amber-500/40"
                        : "bg-white/[0.04] text-white/55 hover:text-white border border-transparent"
                    }`}
                  >
                    #{tag}
                  </button>
                );
              })}
            </div>
          )}
          {prompts.length === 0 && recent.length === 0 ? (
            <div className="px-3 py-3 text-[10px] text-white/40 text-center">
              Brak zapisanych promptów. Wpisz prompt i kliknij "Zapisz".
            </div>
          ) : (
            [...visiblePrompts]
              .sort((a, b) => {
                if (a.starred !== b.starred) return a.starred ? -1 : 1;
                return 0;
              })
              .map((p) => {
                const isRenaming = renamingId === p.id;
                return (
                  <div
                    key={p.id}
                    className="group flex items-start gap-2 px-3 py-2 hover:bg-white/[0.04] transition-colors"
                  >
                    {isRenaming ? (
                      <div className="flex-1 flex items-center gap-1 min-w-0">
                        <input
                          autoFocus
                          value={renameDraft}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              updatePrompt(p.id, {
                                label: renameDraft.trim() || p.text.slice(0, 60),
                              });
                              setRenamingId(null);
                            } else if (e.key === "Escape") {
                              setRenamingId(null);
                            }
                          }}
                          className="flex-1 px-1.5 py-0.5 text-[11px] bg-white/[0.06] border border-white/[0.15] rounded text-white focus:outline-none focus:border-white/30"
                        />
                        <button
                          onClick={() => {
                            updatePrompt(p.id, {
                              label: renameDraft.trim() || p.text.slice(0, 60),
                            });
                            setRenamingId(null);
                          }}
                          className="text-emerald-300 hover:text-emerald-200"
                        >
                          <Check className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            onSelect(p.text);
                            setOpen(false);
                          }}
                          className="flex-1 text-left min-w-0"
                        >
                          <p className="text-[11px] text-white/80 font-medium truncate">
                            {p.label}
                          </p>
                          {p.label !== p.text && (
                            <p className="text-[9px] text-white/40 line-clamp-2 mt-0.5">
                              {summarizeImagePrompt(p.text)}
                            </p>
                          )}
                          {p.tags && p.tags.length > 0 && (
                            <div className="flex flex-wrap gap-0.5 mt-1">
                              {p.tags.map((tag) => (
                                <span key={tag} className="text-[8px] text-amber-200/70">
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            const next = window.prompt(
                              "Tagi (rozdzielone przecinkami):",
                              (p.tags ?? []).join(", ")
                            );
                            if (next === null) return;
                            const tags = next
                              .split(",")
                              .map((s) => s.trim().replace(/^#/, ""))
                              .filter(Boolean);
                            updatePrompt(p.id, { tags });
                          }}
                          className="opacity-0 group-hover:opacity-60 hover:opacity-100 transition-opacity text-[9px] text-amber-200/70 hover:text-amber-200"
                          title="Edytuj tagi"
                        >
                          #
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(p.text);
                            } catch {
                              // ignore
                            }
                          }}
                          className="opacity-0 group-hover:opacity-60 hover:opacity-100 transition-opacity"
                          title="Skopiuj treść promptu do schowka"
                        >
                          <Copy className="w-3 h-3 text-white/60" />
                        </button>
                        <button
                          onClick={() => updatePrompt(p.id, { starred: !p.starred })}
                          className={`transition-opacity ${p.starred ? "opacity-100" : "opacity-0 group-hover:opacity-60 hover:!opacity-100"}`}
                          title={p.starred ? "Odznacz" : "Oznacz jako ulubiony (sticky na górze)"}
                        >
                          <Star
                            className={`w-3 h-3 ${p.starred ? "fill-amber-300 text-amber-300" : "text-white/60"}`}
                          />
                        </button>
                        <button
                          onClick={() => {
                            setRenamingId(p.id);
                            setRenameDraft(p.label);
                          }}
                          className="opacity-0 group-hover:opacity-60 hover:opacity-100 transition-opacity"
                          title="Zmień nazwę"
                        >
                          <Pencil className="w-3 h-3 text-white/60" />
                        </button>
                        <button
                          onClick={() => deletePrompt(p.id)}
                          className="opacity-0 group-hover:opacity-60 hover:opacity-100 transition-opacity"
                          title="Usuń prompt"
                        >
                          <Trash2 className="w-3 h-3 text-red-300" />
                        </button>
                      </>
                    )}
                  </div>
                );
              })
          )}
        </div>
      )}
    </div>
  );
}
